import type { ReactNode } from 'react';

import { Accessibility, AlertTriangle, DollarSign, Fuel } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { DocumentChecklist } from '@/modulos/portal-credenciado/comum/DocumentChecklist';
import { ESTADO_DO_CADASTRO } from '@/modulos/portal-credenciado/comum/recursos';
import { DocValidadeBadge } from '@/modulos/operacao/comum/DocValidadeBadge';
import { ErroApi } from '@/nucleo/auth/cliente-api';
import { mascaraPlaca } from '@/nucleo/formato/documentos';
import { DetailHeader } from '@/ui/DetailHeader';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { CardSkeleton } from '@/ui/base/table-skeleton';

import { CapacidadeDivergenteAlert } from './CapacidadeDivergenteAlert';
import { useVeiculo } from './dados';
import { ROTULO_DO_TIPO_DE_COMBUSTIVEL, aguardaValidacaoDeAncoragens } from './contrato';

/**
 * `/credenciado/veiculos/:slug` — o detalhe de um veículo da frota (o
 * segmento é o id do veículo), com a composição de `PortalVeiculoDetail.tsx`
 * do protótipo: cartão de identificação com a placa, e depois Dados do
 * Veículo, Capacidades, Valor, Seguro e Documentos, o checklist de documentos
 * e Observações, nessa ordem.
 *
 * O dado vem da API (`comum/recursos.ts`). Ela devolve a placa, marca e
 * modelo juntos, o estado do cadastro e os documentos; os demais campos
 * mostram "—", e o que só existe com eles (tipo do veículo, valor mensal,
 * aviso de ancoragens, observações) não aparece.
 *
 * Tudo é consulta. O checklist é só leitura; ver `comum/DocumentChecklist.tsx`.
 */
export function VeiculoDetailPage() {
  const { slug: id } = useParams<{ slug: string }>();
  const { data: veiculo, error, isLoading, isError, refetch } = useVeiculo(id);

  if (error instanceof ErroApi && error.pedeOutroCaminho) {
    return <ErrorState variant="forbidden" description={error.message} />;
  }

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        description="Não foi possível carregar este veículo agora. Tente novamente em instantes."
        primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl space-y-6" role="status" aria-live="polite">
        <CardSkeleton />
        <CardSkeleton />
        <p className="sr-only">Carregando o veículo.</p>
      </div>
    );
  }

  if (!veiculo) {
    return (
      <ErrorState
        variant="resource-not-found"
        title="Veículo não encontrado"
        description="O registro solicitado não existe ou não pertence à sua frota."
        primaryAction={{ label: 'Voltar para Meus Veículos', to: '/credenciado/veiculos' }}
      />
    );
  }

  const placa = mascaraPlaca(veiculo.placa);

  return (
    <div className="max-w-4xl space-y-6">
      <DetailHeader backTo="/credenciado/veiculos" backLabel="Meus Veículos" />

      {aguardaValidacaoDeAncoragens(veiculo) && (
        <div className="flex gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <div className="text-sm text-foreground">
            <p className="font-medium">Ancoragens aguardando validação interna do DTP</p>
            <p className="mt-0.5 text-muted-foreground">
              Este veículo está marcado como Acessível (PCD). O número de ancoragens para cadeira de
              rodas será preenchido após a vistoria interna do DTP. Até lá, o veículo não pode ser
              vinculado a uma OS acessível.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-mono text-xl font-bold">{placa}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {[veiculo.marca_modelo, veiculo.cor].filter(Boolean).join(' • ') || 'Sem descrição'}
              </p>
              {veiculo.crm && (
                <p className="mt-1 text-sm">
                  CRM: <span className="font-medium">{veiculo.crm}</span>
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {veiculo.acessivel === true && (
                <Badge variant="outline" className="gap-1">
                  <Accessibility className="h-3 w-3" aria-hidden="true" /> Acessível
                </Badge>
              )}
              <StatusBadge
                status={ESTADO_DO_CADASTRO[veiculo.situacao].selo}
                label={ESTADO_DO_CADASTRO[veiculo.situacao].rotulo}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Secao titulo="Dados do Veículo">
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Campo rotulo="Placa" valor={placa} monoespacado />
          <Campo rotulo="CRM" valor={veiculo.crm} />
          <Campo rotulo="Marca/Modelo" valor={veiculo.marca_modelo} />
          <Campo rotulo="Cor" valor={veiculo.cor} />
          <Campo
            rotulo="Ano Fab./Mod."
            valor={`${veiculo.ano_fabricacao ?? '—'} / ${veiculo.ano_modelo ?? '—'}`}
          />
          <Campo rotulo="Chassi" valor={veiculo.chassi} monoespacado />
          <Campo rotulo="RENAVAM" valor={veiculo.renavam} monoespacado />
          <div>
            <dt className="flex items-center gap-1 text-xs text-muted-foreground">
              <Fuel className="h-3 w-3" aria-hidden="true" /> Tipo de combustível
            </dt>
            <dd className="text-sm">
              {veiculo.tipo_combustivel
                ? ROTULO_DO_TIPO_DE_COMBUSTIVEL[veiculo.tipo_combustivel]
                : '—'}
            </dd>
          </div>
        </dl>
      </Secao>

      <Secao titulo="Capacidades" conteudoClassName="space-y-3">
        <CapacidadeDivergenteAlert veiculo={veiculo} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <CartaoDeCapacidade rotulo="DETRAN" valor={veiculo.capacidade_detran} />
          <CartaoDeCapacidade rotulo="TEG" valor={veiculo.capacidade_teg} />
          <CartaoDeCapacidade
            rotulo="Total"
            valor={veiculo.capacidade_total}
            fonte={veiculo.capacidade_total_origem}
            dica="Capacidade física total do veículo (assentos de fábrica do chassi, inclui motorista)."
          />
          <CartaoDeCapacidade rotulo="Creche" valor={veiculo.capacidade_creche} />
          <CartaoDeCapacidade
            rotulo="Ancoragens p/ cadeira de rodas"
            valor={veiculo.capacidade_ancoragens_pcd}
            dica="Nº de ancoragens para cadeira de rodas (validado na vistoria interna do DTP)"
          />
        </div>
        {veiculo.acessivel !== null && (
          <div className="mt-4 flex flex-wrap gap-4 border-t border-border pt-4">
            {veiculo.acessivel ? (
              <Badge variant="outline" className="gap-1">
                <Accessibility className="h-3 w-3" aria-hidden="true" /> Acessível (PCD)
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                Não Acessível
              </Badge>
            )}
          </div>
        )}
      </Secao>

      {veiculo.valor_mensal != null && (
        <Secao
          titulo={
            <span className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" aria-hidden="true" /> Valor
            </span>
          }
        >
          <dl>
            <dt className="text-xs text-muted-foreground">Valor Mensal</dt>
            <dd>
              <MoneyDisplay value={veiculo.valor_mensal} className="text-lg font-semibold" />
            </dd>
          </dl>
        </Secao>
      )}

      <Secao titulo="Seguro e Documentos" conteudoClassName="space-y-6">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Seguro</h3>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Campo rotulo="Seguradora" valor={veiculo.seguro_seguradora} />
            <Campo rotulo="Nº Apólice" valor={veiculo.seguro_apolice} />
            <div>
              <dt className="text-xs text-muted-foreground">Validade</dt>
              <dd>
                <DocValidadeBadge validade={veiculo.seguro_validade} />
              </dd>
            </div>
          </dl>
        </div>

        <div className="space-y-2 border-t border-border pt-4">
          <h3 className="text-sm font-semibold">Documentação do Veículo</h3>
          <p className="text-xs text-muted-foreground">
            Validade dos documentos obrigatórios para circulação no TEG.
          </p>
          <dl className="grid gap-4 pt-1 sm:grid-cols-2">
            <div className="space-y-1">
              <dt className="text-sm font-medium">CRLV</dt>
              <dd className="space-y-1">
                <p className="text-xs text-muted-foreground">Licenciamento anual — Detran-SP</p>
                <DocValidadeBadge validade={veiculo.crlv_validade} />
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-sm font-medium">Vistoria ATE</dt>
              <dd className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Autorização de Transporte Escolar — Detran-SP
                </p>
                <DocValidadeBadge validade={veiculo.vistoria_validade} />
              </dd>
            </div>
          </dl>
        </div>

        <SeguroComValor
          titulo="Seguro APP"
          valor={veiculo.seguro_app_valor}
          vencimento={veiculo.seguro_app_vencimento}
        />
        <SeguroComValor
          titulo="Seguro RC"
          valor={veiculo.seguro_rc_valor}
          vencimento={veiculo.seguro_rc_vencimento}
        />
      </Secao>

      <DocumentChecklist title="Documentos do Veículo" itens={veiculo.documentos} />

      {veiculo.observacoes && (
        <Secao titulo="Observações">
          <p className="whitespace-pre-wrap text-sm">{veiculo.observacoes}</p>
        </Secao>
      )}
    </div>
  );
}

/** Cartão de seção do protótipo: `CardHeader` com título `text-base`, sem ícone. */
function Secao({
  titulo,
  conteudoClassName,
  children,
}: {
  titulo: ReactNode;
  conteudoClassName?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="text-base">
          <h2>{titulo}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className={conteudoClassName}>{children}</CardContent>
    </Card>
  );
}

function Campo({
  rotulo,
  valor,
  monoespacado,
}: {
  rotulo: string;
  valor: string | null;
  monoespacado?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{rotulo}</dt>
      <dd className={monoespacado ? 'font-mono text-sm' : 'text-sm'}>{valor || '—'}</dd>
    </div>
  );
}

function SeguroComValor({
  titulo,
  valor,
  vencimento,
}: {
  titulo: string;
  valor: number | null;
  vencimento: string | null;
}) {
  return (
    <div className="space-y-2 border-t border-border pt-4">
      <h3 className="text-sm font-semibold">{titulo}</h3>
      <dl className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">Valor</dt>
          <dd>
            <MoneyDisplay value={valor || null} />
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Vencimento</dt>
          <dd>
            <DocValidadeBadge validade={vencimento} />
          </dd>
        </div>
      </dl>
    </div>
  );
}

function CartaoDeCapacidade({
  rotulo,
  valor,
  fonte,
  dica,
}: {
  rotulo: string;
  valor: number | null;
  fonte?: string | null;
  dica?: string;
}) {
  return (
    <div className="rounded-lg border border-border p-4 text-center" title={dica}>
      <p className="text-3xl font-bold text-primary">{valor ?? '—'}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {rotulo}
        {fonte && (
          <span className="mt-0.5 block text-xs uppercase tracking-wide">Fonte: {fonte}</span>
        )}
      </p>
    </div>
  );
}

export default VeiculoDetailPage;
